import { Handle, type Node, type NodeProps, Position } from '@xyflow/react';
import type { FC } from 'react';
import { RiArrowRightLine } from 'react-icons/ri';
import type { ActionCardNodeData } from './Nodes.utils';
import styles from './Nodes.module.scss';

/**
 * A generic, lighter-weight action node for GraphCanvas: a card showing a `title` over a `subtitle`
 * with a trailing chevron, all read from `node.data`. Reads as a "click to configure" affordance —
 * distinct from `ButtonNode`'s solid dark button, which reads as a terminal run/download action.
 * Consumers wire behaviour via `onClick`, so this component holds no domain knowledge.
 *
 * Exposes a top target handle to connect it to a parent; the handle is visually hidden (read-only
 * graph) but still anchors the incoming edge.
 */
const ActionCardNode: FC<NodeProps<Node<ActionCardNodeData>>> = ({ data }) => {
  const { title, subtitle, disabled = false, onClick } = data;

  return (
    <div className={styles.actionCardWrapper} data-test-component="ActionCardNode">
      <Handle type="target" position={Position.Top} isConnectable={false} className={styles.handle} />
      <button
        type="button"
        className={styles.actionCard}
        disabled={disabled}
        onClick={onClick}
        data-test-element="button"
      >
        <span className={styles.actionCardText}>
          <span className={styles.actionCardTitle} data-test-element="title">
            {title}
          </span>
          <span className={styles.actionCardSubtitle} data-test-element="subtitle">
            {subtitle}
          </span>
        </span>
        <RiArrowRightLine className={styles.actionCardChevron} data-test-element="chevron" />
      </button>
    </div>
  );
};

export default ActionCardNode;
